// src/hooks/useSoldLeads.js
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDisclosure, useToast } from '@chakra-ui/react';
import { db } from '../firebaseConfig';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import useOrgClaims from './useOrgClaims';

/** -----------------------------------------
 * Slot helpers (canonical EN–dash labels)
 * ----------------------------------------- */
const SLOT_OPTIONS = [
  '07:00–09:00',
  '09:00–11:00',
  '11:00–13:00',
  '13:00–15:00',
  '15:00–17:00',
  '17:00–19:00',
  '19:00–21:00',
];

const normaliseSlotLabel = (s) =>
  s ? String(s).trim().replace(/\s*-\s*/g, '–').replace(/\s*–\s*/g, '–') : '';

const slotToString = (raw) => {
  if (raw == null) return '';
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object') return String(raw.label ?? raw.value ?? '');
  return String(raw);
};

const normaliseSlotInput = (raw) => normaliseSlotLabel(slotToString(raw));

const labelFromStart = (hhmm) => {
  if (!hhmm) return '';
  const idx = SLOT_OPTIONS.findIndex((opt) => opt.startsWith(hhmm));
  return idx >= 0 ? SLOT_OPTIONS[idx] : `${hhmm} (Custom)`;

/** -----------------------------------------
 * Email templates
 * ----------------------------------------- */
};
const defaultTemplate = {
  subjectTemplate: 'Survey Request — {{surveyDate}} {{surveySlot}} — {{customerName}} ({{postcode}})',
  bodyTemplate: `Customer: {{customerName}} ({{postcode}})
Phone: {{customerPhone}}
Address: {{address}}

Stage: {{stage}}
Preferred date: {{surveyDate}}
Time slot: {{surveySlot}}
Surveyor: {{surveyor}}

Products: {{productsSummary}}

Special arrangements: {{specialArrangements}}
Door notes: {{doorNotes}}

Attachments: {{attachments}}
Lead: {{leadLink}}

If you cannot attend or you need support/info, please contact {{adminName}} on {{adminPhone}}.

Thanks,
{{adminName}}
{{orgName}} | {{orgPhone}}`,
  defaultRecipients: '',
  includePrice: false,
};

const applyTemplate = (tpl, vars) =>
  (tpl || '')
    .replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, k) => (vars[k] == null ? '' : String(vars[k])))
    .replace(/\n{3,}/g, '\n\n');

const urlEncodeMailtoBody = (str) => encodeURIComponent(str).replace(/%20/g, ' ');

/** -----------------------------------------
 * Stages
 * ----------------------------------------- */
export const STAGE_OPTIONS = [
  { value: 'awaiting_survey', label: 'Awaiting Survey' },
  { value: 'survey_booked', label: 'Survey Booked' },
  { value: 'survey_assigned', label: 'Survey Assigned' },
  { value: 'survey_completed', label: 'Survey Completed' },
  { value: 'costing', label: 'Costing' },
  { value: 'in_manufacture', label: 'In Manufacture' },
  { value: 'fitters_allocated', label: 'Fitters Allocated' },
  { value: 'fitted', label: 'Fitted' },
  { value: 'job_completed', label: 'Job Completed' },
];

export const VISIBLE_STAGE = new Set(['awaiting_survey', 'survey_booked', 'survey_assigned']);

export const stageLabel = (v) => STAGE_OPTIONS.find((o) => o.value === v)?.label || 'Awaiting Survey';

/** -----------------------------------------
 * Row builder (shared by fetch + live)
 * ----------------------------------------- */
function buildRow(orgId, leadId, lead, rep) {
  if (!lead?.apptEndAt) return null; // only finished appointments
  if (!rep) return null;             // require a sales report snapshot

  let nextStage = String(lead.postSaleStage || 'awaiting_survey');
  let surveyDate = lead.surveyDate || '';
  let surveySlot = lead.surveySlot || (lead.surveyTime ? labelFromStart(lead.surveyTime) : '') || '';

  const customerFull =
    (lead.customerFullName && String(lead.customerFullName).trim()) ||
    `${String(lead.customerFirstName || '').trim()} ${String(lead.customerLastName || '').trim()}`.trim();
  const customer = `${lead.customerTitle ? `${lead.customerTitle} ` : ''}${customerFull || ''}`.trim() || '—';

  return {
    id: leadId,
    leadId,
    orgId,

    leadStatus: String(lead.status || 'SOLD').toUpperCase(),
    reportStatus: rep.status || 'draft',

    contractPdfUrl: rep.attachments?.contractPdfUrl || '',
    doorsPdfUrl: rep.attachments?.doorsPdfUrl || '',
    itemsSold: rep.itemsSold || '',
    specialArrangements: rep.specialArrangements || '',
    doorNotes: rep.doorNotes || '',
    fastDelivery: rep.fastDelivery || '',

    customer,
    customerPhone: lead.customerPhone || '',
    postcode: lead.postcode || '—',
    addressLine1: lead.addressLine1 || '',
    addressLine2: lead.addressLine2 || '',
    address: lead.addressLine1 || lead.address || '',

    apptStartAt: lead.apptStartAt || null,
    apptEndAt: lead.apptEndAt || null,

    salespersonId: lead.salespersonId || '',
    salespersonName: lead.salespersonName || '',

    postSaleStage: nextStage,
    surveyDate,
    surveySlot: normaliseSlotInput(surveySlot),
    surveyorName: lead.surveyorName || '',
  };
}

/** -----------------------------------------
 * Hook
 * ----------------------------------------- */
export default function useSoldLeads() {
  const { orgId } = useOrgClaims();
  const toast = useToast();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState(null);

  // Drawer selection
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const editor = useDisclosure();

  // Forward modal state
  const forwardDlg = useDisclosure();
  const [forwardRow, setForwardRow] = useState(null);
  const [forwardRecipients, setForwardRecipients] = useState('');
  const [forwardSubject, setForwardSubject] = useState('');
  const [forwardNote, setForwardNote] = useState('');
  const [savingForward, setSavingForward] = useState(false);
  const [templates, setTemplates] = useState({ survey: defaultTemplate });

  // Cancel modal state
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelLead, setCancelLead] = useState(null);
  const [reasonPreset, setReasonPreset] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [submittingCancel, setSubmittingCancel] = useState(false);

  // Internal: live caches + unsub maps
  const leadCacheRef = useRef(new Map());   // leadId -> lead data
  const repCacheRef = useRef(new Map());    // leadId -> report data
  const repUnsubsRef = useRef(new Map());   // leadId -> unsubscribe()

  /** Load email templates (new path first, legacy fallback) */
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!orgId) return;
      try {
        const tRef = doc(db, 'organizations', orgId, 'settings', 'templates');
        const tSnap = await getDoc(tRef);
        if (alive && tSnap.exists()) {
          const data = tSnap.data() || {};
          // Ensure the 'survey' template always has a fallback
          if (!data.survey) {
            data.survey = defaultTemplate;
          }
          setTemplates(data);
        } else {
          // If no templates doc exists, fallback to default for survey
          setTemplates({ survey: defaultTemplate });
        }
      } catch (e) {
        console.error('Failed to load email templates:', e);
        if (alive) {
          setTemplates({ survey: defaultTemplate });
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [orgId]);

  /** -------- LIVE: SOLD leads (top 25) -------- */
  useEffect(() => {
    if (!orgId) return;

    const repUnsubs = repUnsubsRef.current;
    const repCache  = repCacheRef.current;
    const leadCache = leadCacheRef.current;

    setLoading(true);

    const qRef = query(
      collection(db, 'organizations', orgId, 'leads'),
      where('status', '==', 'SOLD'),
      orderBy('createdAt', 'desc'),
      limit(25)
    );

    const unsubLeads = onSnapshot(
      qRef,
      (snap) => {
        const last = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
        setCursor(last);

        const seenIds = new Set();
        for (const d of snap.docs) {
          seenIds.add(d.id);
          leadCacheRef.current.set(d.id, { id: d.id, ...(d.data() || {}) });

          // Ensure per-lead salesReport/current listener
          if (!repUnsubsRef.current.has(d.id)) {
            const repRef = doc(db, 'organizations', orgId, 'leads', d.id, 'salesReport', 'current');
            const unsubRep = onSnapshot(repRef, (repSnap) => {
              if (repSnap.exists()) {
                repCacheRef.current.set(d.id, repSnap.data() || {});
              } else {
                repCacheRef.current.set(d.id, null);
              }
              // Patch just this row
              const lead = leadCacheRef.current.get(d.id);
              const rep = repCacheRef.current.get(d.id);
              const built = buildRow(orgId, d.id, lead, rep);
              setRows((prev) => {
                const idx = prev.findIndex((r) => r.leadId === d.id);
                if (!built) {
                  return idx >= 0 ? [...prev.slice(0, idx), ...prev.slice(idx + 1)] : prev;
                }
                if (idx < 0) {
                  return [built, ...prev];
                }
                const copy = prev.slice();
                copy[idx] = { ...prev[idx], ...built };
                return copy;
              });
            });
            repUnsubsRef.current.set(d.id, unsubRep);
          }
        }

        // Clean up listeners for leads no longer on the first page
        for (const [leadId, unsub] of repUnsubsRef.current) {
          if (!seenIds.has(leadId)) {
            try { unsub(); } catch {}
            repUnsubsRef.current.delete(leadId);
            repCacheRef.current.delete(leadId);
            leadCacheRef.current.delete(leadId);
          }
        }

        // Build visible rows snapshot from caches (keeps order)
        const builtRows = [];
        for (const d of snap.docs) {
          const lead = leadCacheRef.current.get(d.id);
          const rep = repCacheRef.current.get(d.id);
          const row = buildRow(orgId, d.id, lead, rep);
          if (row) builtRows.push(row);
        }
        setRows(builtRows);
        setLoading(false);
      },
      () => {
        toast({ title: 'Live updates failed', status: 'error' });
        setLoading(false);
      }
    );

    return () => {
      unsubLeads();
      for (const [, unsub] of repUnsubs) {
        try { unsub(); } catch {}
      }
      repUnsubs.clear();
      repCache.clear();
      leadCache.clear();
    };
  }, [orgId, toast]); // eslint-disable-line react-hooks/exhaustive-deps

  /** -------- Paging (older items still manual) -------- */
  const fetchBatch = useCallback(
    async (after) => {
      if (!orgId) return { items: [], lastDoc: null };

      const qRef = after
        ? query(
            collection(db, 'organizations', orgId, 'leads'),
            where('status', '==', 'SOLD'),
            orderBy('createdAt', 'desc'),
            startAfter(after),
            limit(25)
          )
        : query(
            collection(db, 'organizations', orgId, 'leads'),
            where('status', '==', 'SOLD'),
            orderBy('createdAt', 'desc'),
            limit(25)
          );

      const snap = await getDocs(qRef);
      const items = [];

      for (const leadDoc of snap.docs) {
        const lead = { id: leadDoc.id, ...(leadDoc.data() || {}) };
        const repRef = doc(db, 'organizations', orgId, 'leads', leadDoc.id, 'salesReport', 'current');
        const repSnap = await getDoc(repRef);
        if (!repSnap.exists()) continue;
        const rep = repSnap.data() || {};
        const row = buildRow(orgId, leadDoc.id, lead, rep);
        if (row) items.push(row);
      }

      const lastDoc = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
      return { items, lastDoc };
    },
    [orgId]
  );

  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const { items, lastDoc } = await fetchBatch(cursor);
      setRows((prev) => [...prev, ...items]);
      setCursor(lastDoc);
    } catch {
      toast({ title: 'Failed to load more', status: 'error' });
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, fetchBatch, loadingMore, toast]);

  /** ---- Forward helpers ---- */
  const buildTemplate = useCallback((row, template) => {
    const auth = getAuth();
    const user = auth?.currentUser || {};
    const adminName = user.displayName || user.email || 'Admin';
    const adminPhone = user.phoneNumber || '';

    const attachmentsText = [
      row.contractPdfUrl ? 'Contract PDF ✓' : 'Contract PDF —',
      row.doorsPdfUrl ? 'Doors PDF ✓' : 'Doors PDF —',
    ].join(' · ');

    const vars = {
      customerName: row.customer || '',
      customerPhone: row.customerPhone || '',
      postcode: row.postcode || '',
      address: [row.addressLine1 || row.address || '', row.addressLine2 || ''].filter(Boolean).join(', '),
      stage: stageLabel(row.postSaleStage),
      surveyDate: row.surveyDate || '',
      surveySlot: row.surveySlot || '',
      surveyor: row.surveyorName || 'TBC',
      productsSummary: row.itemsSold || '',
      specialArrangements: row.specialArrangements || '',
      doorNotes: row.doorNotes || '',
      attachments: attachmentsText,
      leadLink: `${window.location.origin}/leads/${row.leadId}`,
      adminName,
      adminPhone,
      orgName: '',
      orgPhone: '',
    };
    
    const currentTmpl = template || defaultTemplate;

    return {
      subject: applyTemplate(currentTmpl.subjectTemplate || defaultTemplate.subjectTemplate, vars),
      body: applyTemplate(currentTmpl.bodyTemplate || defaultTemplate.bodyTemplate, vars),
    };
  }, []); // Dependency array is now empty

  const handleCopy = useCallback(
    (row) => {
      try {
        const { subject, body } = buildTemplate(row, templates.survey);
        const text = `${subject}\n\n${body}`;
        navigator.clipboard?.writeText(text);
        toast({ title: 'Brief copied', status: 'success' });
      } catch {
        toast({ title: 'Copy failed', status: 'error' });
      }
    },
    [buildTemplate, templates, toast]
  );

  const handleEmail = useCallback(
    (row, options) => {
      // Default to 'survey' if no template key is provided
      const templateKey = options?.template || 'survey';
      const template = templates[templateKey] || templates.survey;
      
      if (!template) {
          toast({ title: 'Email template not found!', status: 'error' });
          return;
      }

      const { subject, body } = buildTemplate(row, template);
      
      const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${urlEncodeMailtoBody(body)}`;
      
      window.location.href = mailto;
    },
    [buildTemplate, templates, toast] // Update dependencies
  );

  const openForward = useCallback(
    (row) => {
      const surveyTmpl = templates.survey || defaultTemplate;
      const { subject, body } = buildTemplate(row, surveyTmpl);
      
      setForwardRow(row);
      setForwardSubject(subject);
      setForwardNote(body);
      setForwardRecipients(surveyTmpl.defaultRecipients || '');
      forwardDlg.onOpen();
    },
    [buildTemplate, templates, forwardDlg]
  );

  const saveForward = useCallback(async () => {
    if (!orgId || !forwardRow) return;
    try {
      setSavingForward(true);
      const ref = collection(db, 'organizations', orgId, 'leads', forwardRow.leadId, 'surveyDispatches');
      await addDoc(ref, {
        at: serverTimestamp(),
        byUid: null,
        method: 'manual-email',
        recipients: forwardRecipients || '',
        subject: forwardSubject || '',
        note: forwardNote || '',
        surveyDate: forwardRow.surveyDate || '',
        hasContractPdf: !!forwardRow?.contractPdfUrl,
        hasDoorsPdf: !!forwardRow?.doorsPdfUrl,
      });
      toast({ title: 'Forward logged', status: 'success' });
      forwardDlg.onClose();
    } catch {
      toast({ title: 'Failed to log forward', status: 'error' });
    } finally {
      setSavingForward(false);
    }
  }, [orgId, forwardRow, forwardRecipients, forwardSubject, forwardNote, forwardDlg, toast]);

  /** -----------------------------------------
   * Saving – single entry point with optimistic patch
   * ----------------------------------------- */
  const updateRowField = useCallback((leadId, fieldOrPatch, value) => {
    try {
      const patch = typeof fieldOrPatch === 'string' ? { [fieldOrPatch]: value } : (fieldOrPatch || {});
      if (Object.prototype.hasOwnProperty.call(patch, 'surveySlot')) {
        patch.surveySlot = normaliseSlotInput(patch.surveySlot);
      }
      setRows((current) => current.map((r) => (r.leadId === leadId ? { ...r, ...patch } : r)));
    } catch {
      // swallow
    }
  }, []);

  const saveLeadField = useCallback(
    async (leadId, patch) => {
      if (!orgId || !leadId) throw new Error('Organisation or Lead ID is missing.');
      const leadRef = doc(db, 'organizations', orgId, 'leads', leadId);
      const safePatch = { ...patch };
      if (Object.prototype.hasOwnProperty.call(safePatch, 'surveySlot')) {
        safePatch.surveySlot = normaliseSlotInput(safePatch.surveySlot);
      }
      await updateDoc(leadRef, { ...safePatch, updatedAt: serverTimestamp() });
      setRows((currentRows) => currentRows.map((row) => (row.leadId === leadId ? { ...row, ...safePatch } : row)));
    },
    [orgId]
  );

  /** ---- Cancel Sale helpers ---- */
  const openCancelSale = useCallback((row) => {
    setCancelLead(row);
    setReasonPreset('');
    setReasonText('');
    setCancelOpen(true);
  }, []);

  const closeCancelSale = useCallback(() => {
    setCancelOpen(false);
    setCancelLead(null);
  }, []);

  const submitCancelSale = useCallback(async () => {
    if (!orgId || !cancelLead) return;
    try {
      setSubmittingCancel(true);
      const finalReason = reasonPreset === 'Other' ? reasonText.trim() : reasonPreset;
      const fn = httpsCallable(getFunctions(undefined, 'europe-west2'), 'adminCancelLead');
      await fn({ orgId, leadId: cancelLead.leadId, cancelType: 'sale', cancelReason: finalReason });
      setRows((prev) => prev.filter((r) => r.leadId !== cancelLead.leadId));
      toast({ title: 'Sale cancelled', status: 'success' });
      closeCancelSale();
    } catch (e) {
      toast({ title: 'Cancel failed', description: String(e?.message || e), status: 'error' });
      setSubmittingCancel(false);
    }
  }, [orgId, cancelLead, reasonPreset, reasonText, toast, closeCancelSale]);

  /** Bundle handlers (stable) */
  const handlers = useMemo(
    () => ({
      saveLeadField,
      updateRowField,
      handleCopy,
      handleEmail,
      openForward,
      saveForward,
      openCancelSale,
      closeCancelSale,
      submitCancelSale,
    }),
    [
      saveLeadField,
      updateRowField,
      handleCopy,
      handleEmail,
      openForward,
      saveForward,
      openCancelSale,
      closeCancelSale,
      submitCancelSale,
    ]
  );
  
  // Maintain a `tmpl` variable for any legacy dependencies, derived from the new state
  const tmpl = templates.survey || defaultTemplate;

  return {
    // data
    rows,
    loading,
    cursor,
    loadingMore,

    // paging
    loadMore,

    // editor (drawer)
    selectedLeadId,
    setSelectedLeadId,
    editor,

    // templates
    tmpl,

    // forward modal
    forwardDlg,
    forwardRow,
    forwardRecipients,
    forwardSubject,
    forwardNote,
    savingForward,
    setForwardRecipients,
    setForwardSubject,
    setForwardNote,
    saveForward,

    // cancel modal
    cancelOpen,
    cancelLead,
    reasonPreset,
    reasonText,
    submittingCancel,
    setReasonPreset,
    setReasonText,
    openCancelSale,
    closeCancelSale,
    submitCancelSale,

    // unified handlers
    handlers,
  };
}